<template>
  <div class="login-container">
    <div class="login-form">
      <h1>Create account</h1>


      <form @submit.prevent="handleRegister">
        <div class="form-group">
          <input id="email" class="auth-input" v-model="email" type="email" required placeholder="Email" />
        </div>

        <div class="form-group">
          <input id="password" class="auth-input" v-model="password" type="password" :required="!usePasskey" placeholder="Password (or use Passkey)" />
        </div>

        <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:1rem;">
          <label style="display:flex;align-items:center;gap:0.5rem;">
            <input type="checkbox" v-model="usePasskey" /> Use Passkey instead
          </label>
        </div>

        <div style="display:flex;gap:0.5rem;margin-bottom:1rem;">
          <button v-if="!usePasskey" type="submit" :disabled="loading || (!password && !usePasskey)" class="action-btn">{{ loading ? 'Creating...' : 'Create account' }}</button>
          <button v-else type="button" :disabled="loading || !email" class="action-btn" @click="handlePasskeyRegister">{{ loading ? 'Registering...' : 'Register with Passkey' }}</button>
        </div>

        <button type="button" @click="goLogin" class="link-btn">Back to login</button>
      </form>

      <div v-if="error" class="error">{{ error }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Helper to decode base64url to Uint8Array
function decodeBase64urlToUint8Array(base64urlStr: string) {
  // Convert base64url to base64 and pad to length multiple of 4
  let base64 = base64urlStr.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { dbService } from '../services/database'

const router = useRouter()

const email = ref('')
const password = ref('')
const usePasskey = ref(false)
const loading = ref(false)
const error = ref('')

const handleRegister = async () => {
  loading.value = true
  error.value = ''
  try {
    if (usePasskey.value) {
      // Handle passkey registration
      await handlePasskeyRegister();
      return;
    }
    
    if (!password.value) {
      throw new Error('Password required unless using passkey');
    }
    
    console.log('📝 Starting registration for:', email.value);
    
    // Use the new backend registration endpoint
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.value,
        password: password.value,
        name: email.value.split('@')[0],
        type: 'parent'
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Registration failed');
    }
    
    const newUser = await response.json();
    console.log('✅ Registration successful:', newUser);
    
    // Now login with the newly created user
    console.log('🔐 Attempting login after registration...');
    const loginResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.value, password: password.value })
    });
    
    if (loginResponse.ok) {
      const user = await loginResponse.json();
      console.log('✅ User logged in after registration:', user);
      
      // Store session data in sessionStorage (same as Login.vue)
      const sessionData = {
        ...user,
        sessionId: user.sessionToken,
        userId: user.userId || user.id
      };
      sessionStorage.setItem('currentUser', JSON.stringify(sessionData));
      console.log('📦 Session stored in sessionStorage after registration');
      
      router.push('/dashboard');
    } else {
      const errorText = await loginResponse.text();
      throw new Error(`Registration successful but login failed: ${errorText}`);
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Registration failed'
  } finally {
    loading.value = false
  }
}


const handlePasskeyRegister = async () => {
  loading.value = true;
  error.value = '';
  try {
    // Step 1: Request registration challenge from backend
    const challengeRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/auth/webauthn/register/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.value })
    });
    if (!challengeRes.ok) throw new Error('Failed to get passkey registration challenge');
    const challengeData = await challengeRes.json();

    // Step 2: Use WebAuthn API to create credential
    const publicKey = challengeData.publicKeyCredentialCreationOptions;
  publicKey.challenge = decodeBase64urlToUint8Array(publicKey.challenge);
  publicKey.user.id = decodeBase64urlToUint8Array(publicKey.user.id);
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential) throw new Error('Passkey creation failed');
    const cred = credential as PublicKeyCredential;
    const resp = cred.response as AuthenticatorAttestationResponse;

    // Step 3: Send credential to backend for registration
    const credentialData = {
      id: cred.id,
      rawId: btoa(String.fromCharCode(...new Uint8Array(cred.rawId))),
      type: cred.type,
      response: {
        attestationObject: btoa(String.fromCharCode(...new Uint8Array(resp.attestationObject))),
        clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(resp.clientDataJSON)))
      }
    };
    const verifyRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/auth/webauthn/register/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.value, credential: credentialData })
    });
    if (!verifyRes.ok) throw new Error('Passkey registration failed');
    const newUser = await verifyRes.json();
    console.log('User registered with passkey:', newUser);
    // Note: Passkey registration should set session cookies automatically
    router.push('/dashboard');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Passkey registration failed';
  } finally {
    loading.value = false;
  }
}

const goLogin = () => router.push({ name: 'login' })
</script>

<style scoped>
/* reuse login styles */
h1 { text-align:center; margin-bottom:1.5rem; color:#333 }
.login-container {
  min-height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  box-sizing: border-box;
  padding: 0;
}
.login-form {
  background: white;
  padding: 3.5rem 2rem;
  border-radius: 16px;
  border: 1px solid #ddd;
  width: 100vw;
  max-width: 600px;
  min-height: 480px;
  box-sizing: border-box;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
h1 { text-align:center; margin-bottom:1.5rem; color:#333 }
.auth-input { width:100%; padding:0.75rem; border:1px solid #ccc; border-radius:4px; font-size:1rem }
.passkey-btn { padding:0.5rem 0.75rem; border:1px solid #666; background:transparent; color:#333; border-radius:4px; cursor:pointer }
.link-btn { margin-top:0.5rem; background:transparent; border:none; color:#666; cursor:pointer }
button[disabled] { background:#ccc; cursor:not-allowed }
.error { margin-top:1rem; padding:0.75rem; background:#f8f8f8; border:1px solid #ddd; border-radius:4px; color:#333 }
</style>
